import { useTranslation } from"react-i18next";
import { useState, useEffect } from"react";
import axios from"axios";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Badge } from"@/components/ui/badge";
import { Plus, TrendingUp, TrendingDown, DollarSign, AlertCircle, CheckCircle } from"lucide-react";
import { toast } from"sonner";

const BudgetManagementPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [projects, setProjects] = useState([]);
 const [selectedProject, setSelectedProject] = useState("");
 const [currentProject, setCurrentProject] = useState(null);
 const [budgetItems, setBudgetItems] = useState([]);
 const [actualCosts, setActualCosts] = useState([]);
 const [summary, setSummary] = useState(null);
 const [loading, setLoading] = useState(false);
 const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
 const [actualDialogOpen, setActualDialogOpen] = useState(false);
 const [projectJobs, setProjectJobs] = useState([]);

 const [budgetFormData, setBudgetFormData] = useState({
 cost_code:"",
 description:"",
 quantity: 0,
 unit:"pcs",
 unit_price: 0,
 category:"General",
 linked_job_id:"",
 cost_type:"both",
 pricing_mode:"per_unit",
 fixed_total: 0
 });

 const [actualFormData, setActualFormData] = useState({
 cost_code:"",
 description:"",
 amount: 0,
 date: new Date().toISOString().split('T')[0],
 category:"General",
 reference:"",
 notes:""
 });

 const categories = ["General","Materials","Labor","Equipment","Subcontractors","Other"];
 const units = ["pcs","m2","m3","kg","ton","hours","days"];

 useEffect(() => {
 loadProjects();
 }, []);

 useEffect(() => {
 if (selectedProject) {
 loadBudgetData();
 }
 }, [selectedProject]);

 const loadProjects = async () => {
 try {
 const response = await axios.get("/projects");
 setProjects(response.data);
 if (response.data.length > 0) {
 setSelectedProject(response.data[0].id);
 }
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const loadBudgetData = async () => {
 setLoading(true);
 try {
 const [projectRes, budgetRes, actualRes, summaryRes] = await Promise.all([
 axios.get(`/projects/${selectedProject}`),
 axios.get(`/finance/budgets/${selectedProject}`),
 axios.get(`/finance/actual-costs/${selectedProject}`),
 axios.get(`/finance/budgets/${selectedProject}/summary`)
 ]);
 setCurrentProject(projectRes.data);
 setBudgetItems(budgetRes.data);
 setActualCosts(actualRes.data);
 setSummary(summaryRes.data);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const openBudgetDialog = async () => {
 if (selectedProject) {
 try {
 const response = await axios.get(`/project-progress/${selectedProject}`);
 setProjectJobs(response.data || []);
 } catch (error) {
 setProjectJobs([]);
 }
 }
 setBudgetDialogOpen(true);
 };

 const handleJobSelect = (jobId) => {
 const job = projectJobs.find(j => j.id === jobId);
 if (job) {
 const unit = job.measurement_unit === 'pcs' ? 'pcs' : 'm2';
 setBudgetFormData(prev => ({
 ...prev,
 linked_job_id: jobId,
 description: job.job_name,
 quantity: job.area_m2 || 0,
 unit: unit
 }));
 } else {
 setBudgetFormData(prev => ({
 ...prev,
 linked_job_id:"",
 description:"",
 quantity: 0,
 unit:"pcs"
 }));
 }
 };

 const resetBudgetForm = () => {
 setBudgetFormData({
 cost_code:"",
 description:"",
 quantity: 0,
 unit:"pcs",
 unit_price: 0,
 category:"General",
 linked_job_id:"",
 cost_type:"both",
 pricing_mode:"per_unit",
 fixed_total: 0
 });
 };

 const handleBudgetSubmit = async (e) => {
 e.preventDefault();
 try {
 const { cost_type, linked_job_id, pricing_mode, fixed_total, ...baseData } = budgetFormData;
 const category = cost_type ==="materials"?"Materials": cost_type ==="labor"?"Labor":"Materials + Labor";
 
 if (pricing_mode ==="fixed") {
 await axios.post("/finance/budgets", {
 ...baseData,
 project_id: selectedProject,
 quantity: 1,
 unit:"lump sum",
 unit_price: fixed_total,
 category
 });
 } else {
 await axios.post("/finance/budgets", {
 ...baseData,
 project_id: selectedProject,
 category
 });
 }
 
 toast.success(t("messages.saveSuccess"));
 setBudgetDialogOpen(false);
 resetBudgetForm();
 loadBudgetData();
 } catch (error) {
 toast.error(error.response?.data?.detail || t("messages.saveFailed"));
 }
 };

 const handleActualSubmit = async (e) => {
 e.preventDefault();
 try {
 await axios.post("/finance/actual-costs", {
 ...actualFormData,
 project_id: selectedProject,
 date: new Date(actualFormData.date).toISOString()
 });
 toast.success(t("messages.saveSuccess"));
 setActualDialogOpen(false);
 setActualFormData({
 cost_code:"",
 description:"",
 amount: 0,
 date: new Date().toISOString().split('T')[0],
 category:"General",
 reference:"",
 notes:""
 });
 loadBudgetData();
 } catch (error) {
 toast.error(error.response?.data?.detail || t("messages.saveFailed"));
 }
 };

 const formatCurrency = (amount) => {
 return new Intl.NumberFormat('en-US', {
 style: 'currency',
 currency: 'EUR',
 minimumFractionDigits: 0,
 maximumFractionDigits: 0
 }).format(amount || 0);
 };

 if (loading && !summary) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <p className="text-[#94A3B8]">Loading budget data...</p>
 </div>
 </Layout>
 );
 }

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6 p-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-[#0F172A]">
 Budget Management
 </h1>
 <p className="text-[#94A3B8] mt-1">Track planned budget vs actual costs</p>
 </div>
 <div className="w-64">
 <Select value={selectedProject} onValueChange={setSelectedProject}>
 <SelectTrigger>
 <SelectValue placeholder="Select Project"/>
 </SelectTrigger>
 <SelectContent>
 {projects.map((project) => (
 <SelectItem key={project.id} value={project.id}>
 {project.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </div>

 {/* Project Planned Budget Section */}
 {currentProject && (
 <Card className="">
 <CardHeader>
 <CardTitle className="flex items-center justify-between">
 <span>Project Planned Budget</span>
 <Button size="sm"variant="outline"onClick={() => window.location.href = `/projects/${selectedProject}/progress`}>
 Edit in Project Progress →
 </Button>
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
 <div className="bg-white/70 backdrop-blur-xl border border-white/60 rounded-[16px] p-4">
 <div className="text-xs text-[#475569] mb-1">Total Area</div>
 <div className="text-xl font-bold text-[#0F172A]">
 {currentProject.total_m2 ? `${currentProject.total_m2.toLocaleString()} m²` : 'Not set'}
 </div>
 </div>
 <div className="bg-white/70 backdrop-blur-xl border border-white/60 rounded-[16px] p-4">
 <div className="text-xs text-[#475569] mb-1">Cost per m²</div>
 <div className="text-xl font-bold text-[#0F172A]">
 {currentProject.cost_per_m2 ? `${currentProject.cost_per_m2.toLocaleString()} ${currentProject.currency}` : 'Not set'}
 </div>
 </div>
 <div className="bg-white/70 backdrop-blur-xl border border-white/60 rounded-[16px] p-4">
 <div className="text-xs text-[#94A3B8] font-medium mb-1">Land Cost</div>
 <div className="text-xl font-bold text-[#0F172A]">
 {currentProject.land_cost ? `${currentProject.land_cost.toLocaleString()} ${currentProject.currency}` : 'Not set'}
 </div>
 </div>
 <div className="bg-white/70 backdrop-blur-xl border border-white/60 rounded-[16px] p-4">
 <div className="text-xs text-[#94A3B8] font-medium mb-1">Construction Budget</div>
 <div className="text-xl font-bold text-[#0F172A]">
 {currentProject.planned_total_budget 
 ? `${currentProject.planned_total_budget.toLocaleString()} ${currentProject.currency}`
 : (currentProject.total_m2 && currentProject.cost_per_m2)
 ? `${(currentProject.total_m2 * currentProject.cost_per_m2).toLocaleString()} ${currentProject.currency}`
 : 'Not set'}
 </div>
 {currentProject.total_m2 && currentProject.cost_per_m2 && !currentProject.planned_total_budget && (
 <div className="text-[11px] text-[#94A3B8] mt-1">Auto-calculated</div>
 )}
 </div>
 <div className="bg-white/70 backdrop-blur-xl border-2 border-white/60 rounded-[16px] p-4">
 <div className="text-xs text-[#94A3B8] font-bold mb-1">TOTAL PROJECT BUDGET</div>
 <div className="text-2xl font-bold text-[#0F172A]">
 {(() => {
 const construction = currentProject.planned_total_budget || 
 (currentProject.total_m2 && currentProject.cost_per_m2 ? currentProject.total_m2 * currentProject.cost_per_m2 : 0);
 const land = currentProject.land_cost || 0;
 const total = construction + land;
 return total > 0 ? `${total.toLocaleString()} ${currentProject.currency}` : 'Not set';
 })()}
 </div>
 <div className="text-xs text-gray-400 mt-1">Construction + Land</div>
 </div>
 </div>
 {(!currentProject.total_m2 && !currentProject.planned_total_budget) && (
 <div className="mt-4 p-3 bg-white/50 border border-white/60 rounded-lg flex items-start gap-2">
 <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5"/>
 <div className="text-sm text-[#334155]">
 <span className="font-semibold">No planned budget set.</span> Go to Project Progress page and click"Edit"to set your project budget.
 </div>
 </div>
 )}
 </CardContent>
 </Card>
 )}

 {/* Summary Cards */}
 {summary && (
 <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
 <Card className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-xs font-medium text-[#94A3B8]">Total Budget</p>
 <p className="text-2xl font-bold text-[#0F172A] mt-1">
 {formatCurrency(summary.total_budget)}
 </p>
 </div>
 <DollarSign className="h-7 w-7 text-blue-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-xs font-medium text-[#94A3B8]">Actual Costs</p>
 <p className="text-2xl font-bold text-[#0F172A] mt-1">
 {formatCurrency(summary.total_actual)}
 </p>
 </div>
 <TrendingUp className="h-7 w-7 text-amber-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-xs font-medium text-[#94A3B8]">
 Variance
 </p>
 <p className="text-2xl font-bold text-[#0F172A] mt-1">
 {formatCurrency(Math.abs(summary.total_variance))}
 </p>
 <p className="text-[11px] text-[#94A3B8] mt-0.5">{summary.variance_percentage?.toFixed(1)}%</p>
 </div>
 {summary.status === 'under_budget' ? (
 <TrendingDown className="h-7 w-7 text-emerald-600"/>
 ) : (
 <TrendingUp className="h-7 w-7 text-rose-600"/>
 )}
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-xs font-medium text-[#475569]">Status</p>
 <Badge className={`mt-2 ${
 summary.status === 'under_budget' 
 ? 'bg-emerald-500/10 text-emerald-700' 
 : summary.status === 'over_budget'
 ? 'bg-rose-500/10 text-rose-700'
 : 'bg-amber-500/10 text-amber-700'
 }`}>
 {summary.status === 'under_budget' ? 'Under Budget' : 
 summary.status === 'over_budget' ? 'Over Budget' : 'On Budget'}
 </Badge>
 <p className="text-sm text-[#475569] mt-1">
 {Math.abs(summary.variance_percentage).toFixed(1)}%
 </p>
 </div>
 {summary.status === 'under_budget' ? (
 <CheckCircle className="h-7 w-7 text-violet-600"/>
 ) : (
 <AlertCircle className="h-7 w-7 text-violet-600"/>
 )}
 </div>
 </CardContent>
 </Card>
 </div>
 )}

 {/* Planned Budget Section */}
 <Card>
 <CardHeader className="flex flex-row items-center justify-between">
 <div>
 <CardTitle>Planned Budget</CardTitle>
 <p className="text-sm text-[#94A3B8] mt-1">Budgeted line items and costs</p>
 </div>
 <Button onClick={openBudgetDialog}>
 <Plus className="w-4 h-4 mr-2"/>
 Add Budget Item
 </Button>
 </CardHeader>
 <CardContent>
 {budgetItems.length === 0 ? (
 <div className="text-center py-12">
 <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p className="text-[#94A3B8]">No budget items yet</p>
 </div>
 ) : (
 <div className="overflow-x-auto">
 <table className="w-full">
 <thead className="bg-white/50 border-b">
 <tr>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">Code</th>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">Description</th>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">Category</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-[#334155]">Quantity</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-[#334155]">Unit Price</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-[#334155]">Total</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-slate-100">
 {budgetItems.map((item) => (
 <tr key={item.id} className="hover:bg-white/80">
 <td className="py-3 px-4 text-sm font-medium text-[#0F172A]">{item.cost_code}</td>
 <td className="py-3 px-4 text-sm text-[#334155]">{item.description}</td>
 <td className="py-3 px-4">
 <Badge variant="outline">{item.category}</Badge>
 </td>
 <td className="py-3 px-4 text-right text-sm text-[#334155]">
 {item.quantity} {item.unit}
 </td>
 <td className="py-3 px-4 text-right text-sm text-[#334155]">
 {formatCurrency(item.unit_price)}
 </td>
 <td className="py-3 px-4 text-right text-sm font-semibold text-[#0F172A]">
 {formatCurrency(item.total_amount)}
 </td>
 </tr>
 ))}
 <tr className="bg-blue-50/80 font-semibold">
 <td colSpan="5"className="py-3 px-4 text-right text-sm text-[#0F172A]">
 Total Planned Budget:
 </td>
 <td className="py-3 px-4 text-right text-sm text-[#0F172A]">
 {formatCurrency(summary?.total_budget || 0)}
 </td>
 </tr>
 </tbody>
 </table>
 </div>
 )}
 </CardContent>
 </Card>

 {/* Actual Costs Section */}
 <Card>
 <CardHeader className="flex flex-row items-center justify-between">
 <div>
 <CardTitle>Actual Costs</CardTitle>
 <p className="text-sm text-[#94A3B8] mt-1">Real expenses and costs incurred</p>
 </div>
 <Button onClick={() => setActualDialogOpen(true)} variant="outline">
 <Plus className="w-4 h-4 mr-2"/>
 Record Actual Cost
 </Button>
 </CardHeader>
 <CardContent>
 {actualCosts.length === 0 ? (
 <div className="text-center py-12">
 <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p className="text-[#94A3B8]">No actual costs recorded yet</p>
 </div>
 ) : (
 <div className="overflow-x-auto">
 <table className="w-full">
 <thead className="bg-white/50 border-b">
 <tr>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">Date</th>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">Code</th>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">Description</th>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">Category</th>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">Reference</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-[#334155]">Amount</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-slate-100">
 {actualCosts.map((cost) => (
 <tr key={cost.id} className="hover:bg-white/80">
 <td className="py-3 px-4 text-sm text-[#334155]">
 {new Date(cost.date).toLocaleDateString('en-GB')}
 </td>
 <td className="py-3 px-4 text-sm font-medium text-[#0F172A]">{cost.cost_code}</td>
 <td className="py-3 px-4 text-sm text-[#334155]">{cost.description}</td>
 <td className="py-3 px-4">
 <Badge variant="outline">{cost.category}</Badge>
 </td>
 <td className="py-3 px-4 text-sm text-[#94A3B8]">{cost.reference ||"-"}</td>
 <td className="py-3 px-4 text-right text-sm font-semibold text-[#0F172A]">
 {formatCurrency(cost.amount)}
 </td>
 </tr>
 ))}
 <tr className="bg-white/50 font-semibold">
 <td colSpan="5"className="py-3 px-4 text-right text-sm text-[#0F172A]">
 Total Actual Costs:
 </td>
 <td className="py-3 px-4 text-right text-sm text-[#0F172A]">
 {formatCurrency(summary?.total_actual || 0)}
 </td>
 </tr>
 </tbody>
 </table>
 </div>
 )}
 </CardContent>
 </Card>

 {/* Variance Analysis by Category */}
 {summary && summary.category_breakdown && summary.category_breakdown.length > 0 && (
 <Card>
 <CardHeader>
 <CardTitle>Variance Analysis by Category</CardTitle>
 <p className="text-sm text-[#94A3B8] mt-1">Budget vs Actual comparison</p>
 </CardHeader>
 <CardContent>
 <div className="overflow-x-auto">
 <table className="w-full">
 <thead className="bg-white/50 border-b">
 <tr>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">Category</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-[#334155]">Budget</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-[#334155]">Actual</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-[#334155]">Variance</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-[#334155]">%</th>
 <th className="text-center py-3 px-4 text-sm font-semibold text-[#334155]">Status</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-slate-100">
 {summary.category_breakdown.map((cat) => (
 <tr key={cat.category} className="hover:bg-white/80">
 <td className="py-3 px-4 text-sm font-medium text-[#0F172A]">{cat.category}</td>
 <td className="py-3 px-4 text-right text-sm text-[#334155]">
 {formatCurrency(cat.budget)}
 </td>
 <td className="py-3 px-4 text-right text-sm text-[#334155]">
 {formatCurrency(cat.actual)}
 </td>
 <td className={`py-3 px-4 text-right text-sm font-semibold ${
 cat.variance > 0 ? 'text-green-700' : cat.variance < 0 ? 'text-red-700' : 'text-[#334155]'
 }`}>
 {formatCurrency(Math.abs(cat.variance))}
 </td>
 <td className={`py-3 px-4 text-right text-sm ${
 cat.variance > 0 ? 'text-green-700' : cat.variance < 0 ? 'text-red-700' : 'text-[#334155]'
 }`}>
 {Math.abs(cat.variance_percentage).toFixed(1)}%
 </td>
 <td className="py-3 px-4 text-center">
 <Badge className={
 cat.status === 'under' 
 ? 'bg-green-600' 
 : cat.status === 'over' 
 ? 'bg-red-600' 
 : 'bg-gray-600'
 }>
 {cat.status === 'under' ? 'Under' : cat.status === 'over' ? 'Over' : 'On Track'}
 </Badge>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </CardContent>
 </Card>
 )}

 {/* Add Budget Item Dialog */}
 <Dialog open={budgetDialogOpen} onOpenChange={setBudgetDialogOpen}>
 <DialogContent className="max-w-2xl">
 <DialogHeader>
 <DialogTitle>Add Budget Item</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleBudgetSubmit} className="space-y-4">
 {/* Link to Project Task */}
 <div>
 <Label className="text-sm font-semibold">Link to Project Task</Label>
 <Select
 value={budgetFormData.linked_job_id}
 onValueChange={handleJobSelect}
 >
 <SelectTrigger data-testid="job-select">
 <SelectValue placeholder="Select a task to auto-fill..."/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="none">— Manual Entry —</SelectItem>
 {projectJobs.map((job) => (
 <SelectItem key={job.id} value={job.id}>
 {job.job_name}{job.area_m2 ? ` (${job.area_m2.toLocaleString()} ${job.measurement_unit === 'pcs' ? 'pcs' : 'm²'})` : ''}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>

 <div>
 <Label htmlFor="description">Description *</Label>
 <Input
 id="description"
 value={budgetFormData.description}
 onChange={(e) => setBudgetFormData({ ...budgetFormData, description: e.target.value })}
 placeholder="e.g., Tile Installation"
 required
 />
 </div>

 {/* Pricing Mode */}
 <div>
 <Label className="text-sm font-semibold mb-2 block">Pricing Mode</Label>
 <div className="grid grid-cols-2 gap-2">
 {[
 { value:"per_unit", label:"Per Unit (m², pcs, etc.)"},
 { value:"fixed", label:"Fixed Price (Lump Sum)"}
 ].map(opt => (
 <button
 key={opt.value}
 type="button"
 onClick={() => setBudgetFormData(prev => ({
 ...prev,
 pricing_mode: opt.value,
 ...(opt.value === 'fixed' ? { cost_type: 'both' } : {})
 }))}
 className={`py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
 budgetFormData.pricing_mode === opt.value
 ? 'bg-slate-800 text-white border-slate-800'
 : 'bg-white/70 backdrop-blur-sm text-[#334155] border-white/60 hover:border-slate-500'
 }`}
 >
 {opt.label}
 </button>
 ))}
 </div>
 </div>

 {/* Per-Unit: Quantity & Unit */}
 {budgetFormData.pricing_mode ==="per_unit"&& (
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label htmlFor="quantity">Quantity *</Label>
 <Input
 id="quantity"
 type="number"
 step="0.01"
 value={budgetFormData.quantity}
 onChange={(e) => setBudgetFormData({ ...budgetFormData, quantity: parseFloat(e.target.value) || 0 })}
 required
 />
 </div>
 <div>
 <Label htmlFor="unit">Unit *</Label>
 <Select
 value={budgetFormData.unit}
 onValueChange={(value) => setBudgetFormData({ ...budgetFormData, unit: value })}
 >
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 {units.map((unit) => (
 <SelectItem key={unit} value={unit}>{unit}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </div>
 )}

 {/* Cost Type Selection */}
 <div>
 <Label className="text-sm font-semibold mb-2 block">Cost Type *</Label>
 <div className="grid grid-cols-3 gap-2">
 {[
 { value:"both", label:"Materials + Labor"},
 { value:"materials", label:"Materials Only"},
 { value:"labor", label:"Labor Only"}
 ].map(opt => (
 <button
 key={opt.value}
 type="button"
 onClick={() => setBudgetFormData({ ...budgetFormData, cost_type: opt.value })}
 className={`py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
 budgetFormData.cost_type === opt.value
 ? 'bg-blue-600 text-white border-blue-600'
 : 'bg-white/70 backdrop-blur-sm text-[#334155] border-white/60 hover:border-blue-400'
 }`}
 >
 {opt.label}
 </button>
 ))}
 </div>
 </div>

 {/* Price Input */}
 {budgetFormData.pricing_mode ==="fixed"? (
 <div>
 <Label htmlFor="fixed_total">Total Price (€) *</Label>
 <Input
 id="fixed_total"
 type="number"
 step="0.01"
 value={budgetFormData.fixed_total}
 onChange={(e) => setBudgetFormData({ ...budgetFormData, fixed_total: parseFloat(e.target.value) || 0 })}
 placeholder="e.g., 300000"
 required
 />
 </div>
 ) : (
 <div>
 <Label htmlFor="unit_price">Price per {budgetFormData.unit === 'm2' ? 'm²' : budgetFormData.unit} (€) *</Label>
 <Input
 id="unit_price"
 type="number"
 step="0.01"
 value={budgetFormData.unit_price}
 onChange={(e) => setBudgetFormData({ ...budgetFormData, unit_price: parseFloat(e.target.value) || 0 })}
 required
 />
 </div>
 )}

 {/* Total Preview */}
 <div className="bg-blue-50/80 p-4 rounded-lg border border-blue-200/60">
 <p className="text-sm text-[#0F172A]">
 <strong>Total:</strong> {formatCurrency(
 budgetFormData.pricing_mode ==="fixed"
 ? budgetFormData.fixed_total
 : budgetFormData.quantity * budgetFormData.unit_price
 )}
 {budgetFormData.pricing_mode ==="per_unit"&& budgetFormData.quantity > 0 && budgetFormData.unit_price > 0 && (
 <span className="text-[#94A3B8] ml-1">({budgetFormData.quantity.toLocaleString()} × {formatCurrency(budgetFormData.unit_price)})</span>
 )}
 </p>
 </div>

 <div className="flex gap-3 pt-4 border-t">
 <Button type="submit"className="flex-1">Add Budget Item</Button>
 <Button type="button"variant="outline"onClick={() => setBudgetDialogOpen(false)} className="flex-1">
 Cancel
 </Button>
 </div>
 </form>
 </DialogContent>
 </Dialog>

 {/* Record Actual Cost Dialog */}
 <Dialog open={actualDialogOpen} onOpenChange={setActualDialogOpen}>
 <DialogContent className="max-w-2xl">
 <DialogHeader>
 <DialogTitle>Record Actual Cost</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleActualSubmit} className="space-y-4">
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label htmlFor="actual_cost_code">Cost Code *</Label>
 <Input
 id="actual_cost_code"
 value={actualFormData.cost_code}
 onChange={(e) => setActualFormData({ ...actualFormData, cost_code: e.target.value })}
 placeholder="e.g., 01.01.01"
 required
 />
 </div>
 <div>
 <Label htmlFor="actual_category">Category *</Label>
 <Select
 value={actualFormData.category}
 onValueChange={(value) => setActualFormData({ ...actualFormData, category: value })}
 >
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 {categories.map((cat) => (
 <SelectItem key={cat} value={cat}>{cat}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </div>
 <div>
 <Label htmlFor="actual_description">Description *</Label>
 <Input
 id="actual_description"
 value={actualFormData.description}
 onChange={(e) => setActualFormData({ ...actualFormData, description: e.target.value })}
 placeholder="e.g., Invoice for concrete materials"
 required
 />
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label htmlFor="amount">Amount (€) *</Label>
 <Input
 id="amount"
 type="number"
 step="0.01"
 value={actualFormData.amount}
 onChange={(e) => setActualFormData({ ...actualFormData, amount: parseFloat(e.target.value) })}
 required
 />
 </div>
 <div>
 <Label htmlFor="date">Date *</Label>
 <Input
 id="date"
 type="date"
 value={actualFormData.date}
 onChange={(e) => setActualFormData({ ...actualFormData, date: e.target.value })}
 required
 />
 </div>
 </div>
 <div>
 <Label htmlFor="reference">Reference (Invoice/PO Number)</Label>
 <Input
 id="reference"
 value={actualFormData.reference}
 onChange={(e) => setActualFormData({ ...actualFormData, reference: e.target.value })}
 placeholder="e.g., INV-2025-001"
 />
 </div>
 <div>
 <Label htmlFor="notes">{t("common.notes")}</Label>
 <Input
 id="notes"
 value={actualFormData.notes}
 onChange={(e) => setActualFormData({ ...actualFormData, notes: e.target.value })}
 placeholder="Additional notes..."
 />
 </div>
 <div className="flex gap-3 pt-4 border-t">
 <Button type="submit"className="flex-1 bg-orange-600 hover:bg-orange-700">
 Record Cost
 </Button>
 <Button type="button"variant="outline"onClick={() => setActualDialogOpen(false)} className="flex-1">
 Cancel
 </Button>
 </div>
 </form>
 </DialogContent>
 </Dialog>
 </div>
 </Layout>
 );
};

export default BudgetManagementPage;
